/**
 * 13 anomali tespit metodu — gerçek sayısal hesaplamalar (ASKİ m³ zaman serisi).
 * Her metod: { id, name, category, summary, parkResults[] }
 */

const mean = (arr) => (arr.length ? arr.reduce((s, v) => s + v, 0) / arr.length : 0);
const variance = (arr, m = mean(arr)) =>
  arr.length > 1 ? arr.reduce((s, v) => s + (v - m) ** 2, 0) / (arr.length - 1) : 0;
const std = (arr, m = mean(arr)) => Math.sqrt(variance(arr, m));
const median = (arr) => {
  if (!arr.length) return 0;
  const a = [...arr].sort((x, y) => x - y);
  const mid = Math.floor(a.length / 2);
  return a.length % 2 ? a[mid] : (a[mid - 1] + a[mid]) / 2;
};
const quantile = (arr, q) => {
  if (!arr.length) return 0;
  const a = [...arr].sort((x, y) => x - y);
  const pos = (a.length - 1) * q;
  const base = Math.floor(pos);
  const rest = pos - base;
  return a[base + 1] !== undefined ? a[base] + rest * (a[base + 1] - a[base]) : a[base];
};
const round = (n, d = 2) => (n == null || Number.isNaN(n) ? null : Math.round(n * 10 ** d) / 10 ** d);

const fmtDonem = (d) => {
  const s = String(d || '');
  if (s.length >= 6) return `${s.slice(4, 6)}.${s.slice(0, 4)}`;
  return s;
};

/** @param {{donem:any, m3:number}[]} series */
const toValues = (series) => series.map((p) => ({ ...p, m3: Number(p.m3) || 0 }));

function markAnomalies(series, flags, extra = {}) {
  return series.map((p, i) => ({
    donem: p.donem,
    donemLabel: fmtDonem(p.donem),
    m3: round(p.m3, 1),
    isAnomaly: !!flags[i],
    ...Object.fromEntries(Object.entries(extra).map(([k, arr]) => [k, Array.isArray(arr) ? round(arr[i], 3) : arr])),
  }));
}

// ---- 1. Z-Score ----
function methodZScore(series, zThresh = 2) {
  const vals = series.map((p) => p.m3);
  const m = mean(vals);
  const s = std(vals, m) || 1e-9;
  const scores = vals.map((v) => (v - m) / s);
  const flags = scores.map((z) => Math.abs(z) >= zThresh);
  return {
    id: 'zscore',
    name: 'Z-Score',
    category: 'İstatistiksel',
    params: { threshold: `±${zThresh}σ` },
    stats: {
      mean: round(m, 1),
      std: round(s, 1),
      median: round(median(vals), 1),
      min: round(Math.min(...vals), 1),
      max: round(Math.max(...vals), 1),
      n: vals.length,
      anomalyCount: flags.filter(Boolean).length,
    },
    points: markAnomalies(series, flags, { zScore: scores, lower: vals.map(() => m - zThresh * s), upper: vals.map(() => m + zThresh * s) }),
  };
}

// ---- 2. IQR ----
function methodIQR(series, k = 1.5) {
  const vals = series.map((p) => p.m3);
  const q1 = quantile(vals, 0.25);
  const q3 = quantile(vals, 0.75);
  const iqr = q3 - q1;
  const lower = q1 - k * iqr;
  const upper = q3 + k * iqr;
  const flags = vals.map((v) => v < lower || v > upper);
  return {
    id: 'iqr',
    name: 'IQR',
    category: 'İstatistiksel',
    params: { k, formula: 'Q1−1.5×IQR / Q3+1.5×IQR' },
    stats: {
      mean: round(mean(vals), 1),
      median: round(median(vals), 1),
      q1: round(q1, 1),
      q3: round(q3, 1),
      iqr: round(iqr, 1),
      lower: round(lower, 1),
      upper: round(upper, 1),
      n: vals.length,
      anomalyCount: flags.filter(Boolean).length,
    },
    points: markAnomalies(series, flags, { lower: vals.map(() => lower), upper: vals.map(() => upper) }),
  };
}

// ---- 3. Moving Average Deviation ----
function methodMovingAvg(series, window = 3, pct = 0.3) {
  const vals = series.map((p) => p.m3);
  const ma = vals.map((_, i) => {
    if (i < window - 1) return null;
    return mean(vals.slice(i - window + 1, i + 1));
  });
  const flags = vals.map((v, i) => {
    if (ma[i] == null || ma[i] === 0) return false;
    return Math.abs(v - ma[i]) / Math.abs(ma[i]) >= pct;
  });
  const deviations = vals.map((v, i) => (ma[i] ? ((v - ma[i]) / ma[i]) * 100 : null));
  return {
    id: 'moving_avg',
    name: 'Hareketli Ortalama',
    category: 'İstatistiksel',
    params: { window, threshold: `%${pct * 100}` },
    stats: {
      mean: round(mean(vals), 1),
      std: round(std(vals), 1),
      window,
      thresholdPct: pct * 100,
      n: vals.length,
      anomalyCount: flags.filter(Boolean).length,
    },
    points: markAnomalies(series, flags, { movingAvg: ma.map((x) => (x == null ? null : x)), deviationPct: deviations }),
  };
}

// ---- 4. Exponential Moving Average ----
function methodEMA(series, alpha = 0.3, pct = 0.35) {
  const vals = series.map((p) => p.m3);
  const ema = [];
  vals.forEach((v, i) => {
    ema[i] = i === 0 ? v : alpha * v + (1 - alpha) * ema[i - 1];
  });
  const flags = vals.map((v, i) => {
    if (i === 0 || !ema[i]) return false;
    return Math.abs(v - ema[i]) / Math.abs(ema[i] || 1e-9) >= pct;
  });
  const deviations = vals.map((v, i) => ((v - ema[i]) / (ema[i] || 1e-9)) * 100);
  return {
    id: 'exp_ma',
    name: 'Üstel Hareketli Ort.',
    category: 'İstatistiksel',
    params: { alpha, threshold: `%${pct * 100}` },
    stats: {
      mean: round(mean(vals), 1),
      emaLast: round(ema[ema.length - 1], 1),
      alpha,
      n: vals.length,
      anomalyCount: flags.filter(Boolean).length,
    },
    points: markAnomalies(series, flags, { ema, deviationPct: deviations }),
  };
}

// ---- 5. Seasonal (month-of-year residual) ----
function methodSeasonal(series, zThresh = 2) {
  const vals = series.map((p) => p.m3);
  const byMonth = {};
  series.forEach((p) => {
    const m = String(p.donem).slice(4, 6) || '00';
    if (!byMonth[m]) byMonth[m] = [];
    byMonth[m].push(p.m3);
  });
  const monthMean = {};
  Object.keys(byMonth).forEach((m) => { monthMean[m] = mean(byMonth[m]); });
  const residuals = series.map((p) => {
    const m = String(p.donem).slice(4, 6) || '00';
    return p.m3 - (monthMean[m] || mean(vals));
  });
  const rm = mean(residuals);
  const rs = std(residuals, rm) || 1e-9;
  const flags = residuals.map((r) => Math.abs((r - rm) / rs) >= zThresh);
  const seasonalExpected = series.map((p) => {
    const m = String(p.donem).slice(4, 6) || '00';
    return monthMean[m] || mean(vals);
  });
  return {
    id: 'seasonal',
    name: 'Mevsimsel Ayrıştırma',
    category: 'Zaman Serisi',
    params: { residualZ: zThresh },
    stats: {
      mean: round(mean(vals), 1),
      residualMean: round(rm, 1),
      residualStd: round(rs, 1),
      months: Object.keys(monthMean).length,
      n: vals.length,
      anomalyCount: flags.filter(Boolean).length,
    },
    points: markAnomalies(series, flags, { seasonalExpected, residual: residuals }),
  };
}

/** Deterministik PRNG — Isolation Forest sonuçları tekrarlanabilir olsun */
function mulberry32(seed) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// ---- 6. Isolation Forest (1D simplified) ----
function isolationScore1D(vals, samples = 100, heightLimit = 8, seed = 42) {
  const n = vals.length;
  if (n < 2) return vals.map(() => 0);
  const rand = mulberry32(seed);
  const scores = new Array(n).fill(0);
  for (let t = 0; t < samples; t++) {
    const path = new Array(n).fill(0);
    const isolate = (idxs, depth) => {
      if (idxs.length <= 1 || depth >= heightLimit) {
        idxs.forEach((i) => { path[i] += depth; });
        return;
      }
      const subset = idxs.map((i) => vals[i]);
      const lo = Math.min(...subset);
      const hi = Math.max(...subset);
      if (lo === hi) {
        idxs.forEach((i) => { path[i] += depth; });
        return;
      }
      const split = lo + rand() * (hi - lo);
      const left = idxs.filter((i) => vals[i] < split);
      const right = idxs.filter((i) => vals[i] >= split);
      isolate(left, depth + 1);
      isolate(right, depth + 1);
    };
    isolate([...Array(n).keys()], 0);
    path.forEach((p, i) => { scores[i] += p; });
  }
  return scores.map((s) => s / samples);
}

function methodIsolationForest(series) {
  const vals = series.map((p) => p.m3);
  const avgPath = isolationScore1D(vals);
  const m = mean(avgPath);
  // Kısa path = daha anomali → score yüksek olsun diye ters çevir
  const anomalyScore = avgPath.map((p) => (m - p) / (std(avgPath, m) || 1e-9));
  const thresh = 1.5;
  const flags = anomalyScore.map((s) => s >= thresh);
  return {
    id: 'isolation_forest',
    name: 'Isolation Forest',
    category: 'Makine Öğrenmesi',
    params: { pathThreshold: thresh },
    stats: {
      mean: round(mean(vals), 1),
      meanPathLength: round(m, 2),
      n: vals.length,
      anomalyCount: flags.filter(Boolean).length,
    },
    points: markAnomalies(series, flags, { pathLength: avgPath, anomalyScore }),
  };
}

// ---- 7. LOF (1D) ----
function methodLOF(series, k = 3) {
  const vals = series.map((p) => p.m3);
  const n = vals.length;
  const kk = Math.max(1, Math.min(k, n - 1));
  const lof = vals.map((v, i) => {
    const dists = vals
      .map((u, j) => ({ j, d: Math.abs(v - u) }))
      .filter((x) => x.j !== i)
      .sort((a, b) => a.d - b.d)
      .slice(0, kk);
    const kDist = dists[dists.length - 1]?.d || 1e-9;
    const lrd = 1 / (mean(dists.map((d) => Math.max(d.d, kDist))) || 1e-9);
    const neighborLrd = dists.map(({ j }) => {
      const nd = vals
        .map((u, t) => ({ t, d: Math.abs(vals[j] - u) }))
        .filter((x) => x.t !== j)
        .sort((a, b) => a.d - b.d)
        .slice(0, kk);
      const nk = nd[nd.length - 1]?.d || 1e-9;
      return 1 / (mean(nd.map((d) => Math.max(d.d, nk))) || 1e-9);
    });
    return mean(neighborLrd) / (lrd || 1e-9);
  });
  const flags = lof.map((s) => s >= 1.5);
  return {
    id: 'lof',
    name: 'Local Outlier Factor',
    category: 'Makine Öğrenmesi',
    params: { k: kk, threshold: 1.5 },
    stats: {
      mean: round(mean(vals), 1),
      meanLOF: round(mean(lof), 3),
      n: vals.length,
      anomalyCount: flags.filter(Boolean).length,
    },
    points: markAnomalies(series, flags, { lof }),
  };
}

// ---- 8. CUSUM ----
function methodCUSUM(series, k = 0.5, h = 5) {
  const vals = series.map((p) => p.m3);
  const m = mean(vals);
  const s = std(vals, m) || 1e-9;
  const target = m;
  let gp = 0;
  let gn = 0;
  const gPlus = [];
  const gMinus = [];
  const flags = [];
  vals.forEach((v) => {
    const z = (v - target) / s;
    gp = Math.max(0, gp + z - k);
    gn = Math.max(0, gn - z - k);
    gPlus.push(gp);
    gMinus.push(gn);
    flags.push(gp > h || gn > h);
  });
  return {
    id: 'cusum',
    name: 'CUSUM',
    category: 'Zaman Serisi',
    params: { k, h },
    stats: {
      mean: round(m, 1),
      std: round(s, 1),
      maxCusumPlus: round(Math.max(...gPlus), 2),
      maxCusumMinus: round(Math.max(...gMinus), 2),
      n: vals.length,
      anomalyCount: flags.filter(Boolean).length,
    },
    points: markAnomalies(series, flags, { cusumPlus: gPlus, cusumMinus: gMinus }),
  };
}

// ---- 9. DBSCAN 1D ----
function methodDBSCAN(series, epsFactor = 0.35, minPts = 2) {
  const vals = series.map((p) => p.m3);
  const s = std(vals) || 1e-9;
  const eps = Math.max(epsFactor * s, 1e-6);
  const n = vals.length;

  const regionQuery = (i) => {
    const neighbors = [];
    for (let j = 0; j < n; j++) {
      if (Math.abs(vals[i] - vals[j]) <= eps) neighbors.push(j);
    }
    return neighbors;
  };

  const visited = new Array(n).fill(false);
  const lab = new Array(n).fill(-1);
  let cid = 0;
  for (let i = 0; i < n; i++) {
    if (visited[i]) continue;
    visited[i] = true;
    const neighbors = regionQuery(i);
    if (neighbors.length < minPts) {
      lab[i] = -1;
      continue;
    }
    lab[i] = cid;
    const queue = neighbors.filter((x) => x !== i);
    while (queue.length) {
      const p = queue.shift();
      if (!visited[p]) {
        visited[p] = true;
        const pn = regionQuery(p);
        if (pn.length >= minPts) {
          pn.forEach((q) => { if (!queue.includes(q) && !visited[q]) queue.push(q); });
        }
      }
      if (lab[p] < 0) lab[p] = cid;
    }
    cid++;
  }

  const flags = lab.map((l) => l === -1);
  return {
    id: 'dbscan',
    name: 'DBSCAN',
    category: 'Makine Öğrenmesi',
    params: { eps: round(eps, 1), minPts },
    stats: {
      mean: round(mean(vals), 1),
      std: round(s, 1),
      eps: round(eps, 1),
      clusters: cid,
      n: vals.length,
      anomalyCount: flags.filter(Boolean).length,
    },
    points: markAnomalies(series, flags, { cluster: lab }),
  };
}

// ---- 10. Grubbs ----
function methodGrubbs(series, alpha = 0.05) {
  // Iterative single-outlier Grubbs (simplified critical value approx)
  const seriesCopy = toValues(series);
  const flags = new Array(seriesCopy.length).fill(false);
  const scores = new Array(seriesCopy.length).fill(0);
  const remaining = seriesCopy.map((p, i) => ({ ...p, idx: i }));

  // Run up to 5 outliers
  for (let iter = 0; iter < 5 && remaining.length > 3; iter++) {
    const vals = remaining.map((p) => p.m3);
    const m = mean(vals);
    const s = std(vals, m) || 1e-9;
    let maxG = -1;
    let maxIdx = -1;
    remaining.forEach((p, ri) => {
      const g = Math.abs(p.m3 - m) / s;
      scores[p.idx] = g;
      if (g > maxG) { maxG = g; maxIdx = ri; }
    });
    const n = vals.length;
    // Approximate critical value for alpha~0.05
    const tCrit = 1.96 + 2.5 / Math.sqrt(n); // rough
    const gCrit = ((n - 1) / Math.sqrt(n)) * Math.sqrt((tCrit * tCrit) / (n - 2 + tCrit * tCrit));
    if (maxG > gCrit) {
      flags[remaining[maxIdx].idx] = true;
      remaining.splice(maxIdx, 1);
    } else break;
  }

  const vals = series.map((p) => p.m3);
  return {
    id: 'grubbs',
    name: 'Grubbs Testi',
    category: 'İstatistiksel',
    params: { alpha },
    stats: {
      mean: round(mean(vals), 1),
      std: round(std(vals), 1),
      maxG: round(Math.max(...scores), 3),
      n: vals.length,
      anomalyCount: flags.filter(Boolean).length,
    },
    points: markAnomalies(series, flags, { gScore: scores }),
  };
}

// ---- 11. MAD ----
function methodMAD(series, thresh = 3) {
  const vals = series.map((p) => p.m3);
  const med = median(vals);
  const absDev = vals.map((v) => Math.abs(v - med));
  const mad = median(absDev) || 1e-9;
  const robustZ = absDev.map((d) => (0.6745 * d) / mad);
  const flags = robustZ.map((z) => z >= thresh);
  return {
    id: 'mad',
    name: 'MAD',
    category: 'İstatistiksel',
    params: { threshold: thresh },
    stats: {
      mean: round(mean(vals), 1),
      median: round(med, 1),
      mad: round(mad, 1),
      n: vals.length,
      anomalyCount: flags.filter(Boolean).length,
    },
    points: markAnomalies(series, flags, { robustZ }),
  };
}

// ---- 12. STL-like (seasonal + trend residual) ----
function methodSTL(series, zThresh = 2.5) {
  const vals = series.map((p) => p.m3);
  const n = vals.length;
  // Trend: centered moving average window 5
  const w = 5;
  const trend = vals.map((_, i) => {
    const a = Math.max(0, i - Math.floor(w / 2));
    const b = Math.min(n, i + Math.floor(w / 2) + 1);
    return mean(vals.slice(a, b));
  });
  const detrended = vals.map((v, i) => v - trend[i]);
  const byMonth = {};
  series.forEach((p, i) => {
    const m = String(p.donem).slice(4, 6) || '00';
    if (!byMonth[m]) byMonth[m] = [];
    byMonth[m].push(detrended[i]);
  });
  const seasonalComp = series.map((p) => {
    const m = String(p.donem).slice(4, 6) || '00';
    return mean(byMonth[m] || [0]);
  });
  const residual = vals.map((v, i) => v - trend[i] - seasonalComp[i]);
  const rm = mean(residual);
  const rs = std(residual, rm) || 1e-9;
  const flags = residual.map((r) => Math.abs((r - rm) / rs) >= zThresh);
  return {
    id: 'stl',
    name: 'STL Ayrıştırma',
    category: 'Zaman Serisi',
    params: { residualZ: zThresh },
    stats: {
      mean: round(mean(vals), 1),
      residualMean: round(rm, 1),
      residualStd: round(rs, 1),
      n: vals.length,
      anomalyCount: flags.filter(Boolean).length,
    },
    points: markAnomalies(series, flags, { trend, seasonal: seasonalComp, residual }),
  };
}

// ---- 13. ML Hibrit (Prophet-like: trend + monthly seasonality) ----
function methodProphetLike(series, zThresh = 2) {
  const vals = series.map((p) => p.m3);
  const n = vals.length;
  // Linear trend via least squares on index
  const xs = vals.map((_, i) => i);
  const xMean = mean(xs);
  const yMean = mean(vals);
  const num = xs.reduce((s, x, i) => s + (x - xMean) * (vals[i] - yMean), 0);
  const den = xs.reduce((s, x) => s + (x - xMean) ** 2, 0) || 1e-9;
  const slope = num / den;
  const intercept = yMean - slope * xMean;
  const trend = xs.map((x) => intercept + slope * x);

  const byMonth = {};
  series.forEach((p, i) => {
    const m = String(p.donem).slice(4, 6) || '00';
    if (!byMonth[m]) byMonth[m] = [];
    byMonth[m].push(vals[i] - trend[i]);
  });
  const season = series.map((p) => {
    const m = String(p.donem).slice(4, 6) || '00';
    return mean(byMonth[m] || [0]);
  });
  const forecast = trend.map((t, i) => t + season[i]);
  const residual = vals.map((v, i) => v - forecast[i]);
  const rm = mean(residual);
  const rs = std(residual, rm) || 1e-9;
  const flags = residual.map((r) => Math.abs((r - rm) / rs) >= zThresh);
  return {
    id: 'ml_anomaly',
    name: 'ML Hibrit (Prophet)',
    category: 'Makine Öğrenmesi',
    params: { residualZ: zThresh },
    stats: {
      mean: round(mean(vals), 1),
      trendSlope: round(slope, 3),
      residualStd: round(rs, 1),
      mae: round(mean(residual.map(Math.abs)), 1),
      n: vals.length,
      anomalyCount: flags.filter(Boolean).length,
    },
    points: markAnomalies(series, flags, { forecast, residual, trend }),
  };
}

export const METHOD_RUNNERS = [
  methodZScore,
  methodIQR,
  methodMovingAvg,
  methodEMA,
  methodSeasonal,
  methodIsolationForest,
  methodLOF,
  methodCUSUM,
  methodDBSCAN,
  methodGrubbs,
  methodMAD,
  methodSTL,
  methodProphetLike,
];

export const METHOD_META = [
  { id: 'zscore', name: 'Z-Score', category: 'İstatistiksel' },
  { id: 'iqr', name: 'IQR', category: 'İstatistiksel' },
  { id: 'moving_avg', name: 'Hareketli Ortalama', category: 'İstatistiksel' },
  { id: 'exp_ma', name: 'Üstel Hareketli Ort.', category: 'İstatistiksel' },
  { id: 'seasonal', name: 'Mevsimsel Ayrıştırma', category: 'Zaman Serisi' },
  { id: 'isolation_forest', name: 'Isolation Forest', category: 'Makine Öğrenmesi' },
  { id: 'lof', name: 'Local Outlier Factor', category: 'Makine Öğrenmesi' },
  { id: 'cusum', name: 'CUSUM', category: 'Zaman Serisi' },
  { id: 'dbscan', name: 'DBSCAN', category: 'Makine Öğrenmesi' },
  { id: 'grubbs', name: 'Grubbs Testi', category: 'İstatistiksel' },
  { id: 'mad', name: 'MAD', category: 'İstatistiksel' },
  { id: 'stl', name: 'STL Ayrıştırma', category: 'Zaman Serisi' },
  { id: 'ml_anomaly', name: 'ML Hibrit (Prophet)', category: 'Makine Öğrenmesi' },
];

/** Her metodun gerçek hesap adımı — UI’da “nasıl hesaplanıyor” paneli */
export const METHOD_EXPLAIN = {
  zscore: {
    how: 'Her dönem m³ için z = (x − μ) / σ hesaplanır. |z| ≥ 2 ise anomali.',
    formula: 'z = (x − μ) / σ ; anomali ⇔ |z| ≥ 2',
    inputs: 'Seri: dönemsel m³ (x₁…xₙ)',
    outputs: 'zScore, lower=μ−2σ, upper=μ+2σ, isAnomaly',
    steps: ['Serinin ortalaması μ ve std σ alınır', 'Her dönem için z-skoru üretilir', 'Eşik dışı dönemler kırmızı işaretlenir'],
    ai: false,
  },
  iqr: {
    how: 'Q1–Q3 aralığı (IQR) ile sınırlar: Q1−1.5×IQR ve Q3+1.5×IQR. Dışında kalanlar anomali.',
    formula: 'IQR = Q3−Q1 ; alt = Q1−1.5×IQR ; üst = Q3+1.5×IQR',
    inputs: 'Seri m³ değerleri',
    outputs: 'q1,q3,iqr,lower,upper,isAnomaly',
    steps: ['Çeyrekler (Q1, Q3) hesaplanır', 'Alt/üst çit çizilir', 'Çit dışı tüketimler işaretlenir'],
    ai: false,
  },
  moving_avg: {
    how: '3 dönemlik hareketli ortalamadan %30+ sapma anomali sayılır.',
    formula: 'MA₃(t) ; sapma = |x−MA|/MA ; anomali ⇔ sapma ≥ 0.30',
    inputs: 'm³ serisi, pencere=3',
    outputs: 'ma, sapmaOrani, isAnomaly',
    steps: ['Pencere ortalaması hesaplanır', 'Mutlak sapma oranı bakılır', 'Eşik aşımı işaretlenir'],
    ai: false,
  },
  exp_ma: {
    how: 'Üstel hareketli ortalama (EMA) tahmini ile gerçek m³ farkı; büyük sapmalar anomali.',
    formula: 'EMAₜ = α·xₜ + (1−α)·EMAₜ₋₁ ; residual = x − EMA',
    inputs: 'm³ serisi, α≈0.3',
    outputs: 'ema, residual, isAnomaly',
    steps: ['EMA güncellenir', 'Residual = gerçek − EMA', 'Residual eşiği aşanlar işaretlenir'],
    ai: false,
  },
  seasonal: {
    how: 'Aynı ayın tarihsel ortalaması “beklenen” alınır; residual z-skoru ile anomali.',
    formula: 'beklenen = ayOrt[ay(t)] ; r = x−beklenen ; zᵣ = r/σᵣ',
    inputs: 'm³ + dönem (YYYYMM)',
    outputs: 'beklenen, residual, residualZ, isAnomaly',
    steps: ['Ay bazlı mevsim profili çıkarılır', 'Beklenen − gerçek residual', 'Residual z ≥ eşik → anomali'],
    ai: false,
  },
  isolation_forest: {
    how: 'Rastgele bölünmüş izolasyon ağaçlarında kısa path = kolay ayrılan = anomali (ML).',
    formula: 'pathLength(x) ; anomalyScore ∝ −pathLength ; eşik ≈ meanPath ± 1.5σ',
    inputs: 'm³ noktaları, 100 ağaç',
    outputs: 'pathLength, anomalyScore, isAnomaly',
    steps: ['100 ağaç, her noktaya path uzunluğu', 'Kısa path → yüksek anomalyScore', 'Skor ≥ 1.5σ anomali'],
    ai: true,
  },
  lof: {
    how: 'Local Outlier Factor: noktanın yoğunluğu komşularına göre düşükse anomali (ML).',
    formula: 'LOF(x) = ortalama(LRD(komşu)) / LRD(x) ; anomali ⇔ LOF ≥ 1.5',
    inputs: 'm³, k-NN',
    outputs: 'lof, isAnomaly',
    steps: ['k-en yakın komşu mesafeleri', 'Yerel erişilebilirlik yoğunluğu (LRD)', 'LOF ≥ 1.5 → anomali'],
    ai: true,
  },
  cusum: {
    how: 'Kümülatif toplam sapma (CUSUM+ / CUSUM−); eşik h aşılırsa kayma/anomali.',
    formula: 'S⁺ = max(0, S⁺ + z − k) ; S⁻ = max(0, S⁻ − z − k) ; anomali ⇔ S≥h',
    inputs: 'z-skoru akışı',
    outputs: 'cusumPlus, cusumMinus, isAnomaly',
    steps: ['z-skoru akışı', 'Pozitif/negatif CUSUM birikir', 'h eşiği aşımı işaretlenir'],
    ai: false,
  },
  dbscan: {
    how: 'Yoğunluk kümeleme: bir kümeye düşmeyen noktalar (noise) anomali (ML).',
    formula: 'eps = 0.35σ ; cluster=-1 ⇒ noise/anomali',
    inputs: 'm³, eps, minPts',
    outputs: 'cluster, isAnomaly',
    steps: ['eps = 0.35σ komşuluk', 'minPts ile çekirdek noktalar', 'cluster = −1 → anomali'],
    ai: true,
  },
  grubbs: {
    how: 'Grubbs testi: en uç değer istatistiksel olarak outlier mı diye iteratif kontrol.',
    formula: 'G = max|x−μ|/σ ; G > G_kritik ⇒ outlier',
    inputs: 'm³ serisi',
    outputs: 'G, isAnomaly (işaretlenen uçlar)',
    steps: ['G = max|x−μ|/σ', 'Kritik değerle karşılaştır', 'Outlier çıkar, tekrar et (max 5)'],
    ai: false,
  },
  mad: {
    how: 'Medyan mutlak sapma: |x − medyan| / (1.4826×MAD) ≥ 3 → anomali (robust).',
    formula: 'MAD = medyan(|x−medyan|) ; Mz = |x−med|/ (1.4826·MAD)',
    inputs: 'm³ serisi',
    outputs: 'modifiedZ, isAnomaly',
    steps: ['Medyan ve MAD', 'Modified z-score', 'Eşik 3 ile işaretleme'],
    ai: false,
  },
  stl: {
    how: 'Trend + mevsim + residual ayrıştırması; residual z-skoru ile anomali.',
    formula: 'x = trend + mevsim + residual ; zᵣ = residual/σᵣ',
    inputs: 'm³ zaman serisi',
    outputs: 'trend, seasonal, residual, residualZ, isAnomaly',
    steps: ['Trend (hareketli) + mevsim', 'Residual = x − trend − mevsim', 'Residual z eşiği'],
    ai: false,
  },
  ml_anomaly: {
    how: 'Prophet-benzeri hibrit: lineer trend + ay mevsimi → forecast; residual z ile anomali (ML).',
    formula: 'ŷ = a + b·t + mevsim(ay) ; r = x−ŷ ; zᵣ = r/σᵣ',
    inputs: 'm³ + dönem indeksi',
    outputs: 'forecast, residual, residualZ, isAnomaly',
    steps: ['Least-squares trend eğimi', 'Ay bazlı mevsim eklenir', 'forecast residual z ≥ eşik → anomali'],
    ai: true,
  },
};

/** Dönem satırı için okunabilir hesap notu */
export function explainPoint(methodId, point, stats = {}, params = {}) {
  if (!point) return '-';
  const x = point.m3;
  const flag = point.isAnomaly ? 'ANOMALİ' : 'normal';
  switch (methodId) {
    case 'zscore':
      return `z=(${fmtN(x)}−${fmtN(stats.mean)})/${fmtN(stats.std)}=${fmtN(point.zScore)} ; eşik ±2 → ${flag}`;
    case 'iqr':
      return `x=${fmtN(x)} ; çit [${fmtN(point.lower)}, ${fmtN(point.upper)}] → ${flag}`;
    case 'moving_avg':
      return `MA=${fmtN(point.movingAvg)} ; sapma%=${fmtN(point.deviationPct)} → ${flag}`;
    case 'exp_ma':
      return `EMA=${fmtN(point.ema)} ; sapma%=${fmtN(point.deviationPct)} → ${flag}`;
    case 'seasonal':
      return `beklenen=${fmtN(point.seasonalExpected)} ; residual=${fmtN(point.residual)} → ${flag}`;
    case 'isolation_forest':
      return `path=${fmtN(point.pathLength)} ; score=${fmtN(point.anomalyScore)} → ${flag}`;
    case 'lof':
      return `LOF=${fmtN(point.lof)} (eşik 1.5) → ${flag}`;
    case 'cusum':
      return `S+=${fmtN(point.cusumPlus)} ; S-=${fmtN(point.cusumMinus)} → ${flag}`;
    case 'dbscan':
      return `cluster=${point.cluster} (−1=noise) → ${flag}`;
    case 'grubbs':
      return `G=${fmtN(point.gScore)} ; işaret=${point.isAnomaly ? 'outlier' : 'değil'} → ${flag}`;
    case 'mad':
      return `robustZ=${fmtN(point.robustZ)} (eşik 3) → ${flag}`;
    case 'stl':
      return `trend=${fmtN(point.trend)} ; mevsim=${fmtN(point.seasonal)} ; residual=${fmtN(point.residual)} → ${flag}`;
    case 'ml_anomaly':
      return `ŷ=${fmtN(point.forecast)} ; residual=${fmtN(point.residual)} ; trend=${fmtN(point.trend)} → ${flag}`;
    default:
      return `${flag} · x=${fmtN(x)}`;
  }
}

function fmtN(n) {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return '—';
  return Number(n).toLocaleString('tr-TR', { maximumFractionDigits: 3 });
}

/**
 * @param {{donem:any, m3:number}[]} series chronologically sorted
 */
export function runAllMethods(series) {
  const cleaned = toValues(series).filter((p) => p.donem != null);
  cleaned.sort((a, b) => String(a.donem).localeCompare(String(b.donem)));
  if (cleaned.length < 3) {
    return METHOD_RUNNERS.map((fn, i) => ({
      ...METHOD_META[i],
      stats: { n: cleaned.length, anomalyCount: 0, mean: round(mean(cleaned.map((p) => p.m3)), 1) },
      points: [],
      params: {},
      insufficient: true,
    }));
  }
  return METHOD_RUNNERS.map((fn) => fn(cleaned));
}

export function seriesStats(series) {
  const vals = toValues(series).map((p) => p.m3);
  if (!vals.length) return null;
  return {
    n: vals.length,
    mean: round(mean(vals), 1),
    median: round(median(vals), 1),
    std: round(std(vals), 1),
    min: round(Math.min(...vals), 1),
    max: round(Math.max(...vals), 1),
    q1: round(quantile(vals, 0.25), 1),
    q3: round(quantile(vals, 0.75), 1),
    total: round(vals.reduce((s, v) => s + v, 0), 1),
  };
}
